import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import ProgressWizard from 'components/field/ProgressWizard';
import StringInput from 'components/field/StringInput';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Tabs from 'components/global/Tabs';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';
import Maps from 'components/maps/Maps';

import useAddressData from 'hooks/useAddressData';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { ADMIN, AUTOCOMPLETE } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import { faAngleLeft, faAngleRight, faBoxCheck, faUserTag } from '@fortawesome/pro-solid-svg-icons';

import * as S from './PolygonCoverage.styled';

const defaultColSpan = {
	md: 6
};

const validateOptions = {
	shouldValidate: true,
	shouldDirty: true
};

const PolygonCoverageForm = NiceModal.create(({ id, mode, modalName }) => {
	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();
	const { closeModal } = useModalRouter();
	const modal = useModal();
	const { showError, showSuccess } = useAlert();

	const [tab, setTab] = useState(0);
	const [map, setMap] = useState(null);
	const maps = window?.google?.maps;

	const addMode = useMemo(() => mode === 'add', [mode]);
	const viewMode = useMemo(() => mode === 'view', [mode]);

	const nextStep = () => {
		if (tab < 1) {
			setTab(tab + 1);
		}
	};

	const prevStep = () => {
		if (tab > 0) {
			setTab(tab - 1);
		}
	};

	const polygonInfoSchema = useMemo(
		() =>
			yup.object().shape({
				polygonName: yup.string().nullable().required(),
				connectionName: yup.string().nullable().required(),
				country: yup.string().nullable().required(),
				city: yup.string().nullable().required()
			}),
		[]
	);

	const polygonSchema = useMemo(
		() =>
			yup
				.object()
				.shape({
					polygon: yup.string().polygon().required()
				})
				.required(),
		[]
	);

	const coordinatesToStringArray = useCallback(
		(coords) =>
			coords.reduce((acc, coord) => {
				const coordStr = `${coord.lat} ${coord.lng}`;
				if (acc) {
					return `${acc},${coordStr}`;
				} else {
					return coordStr;
				}
			}, ''),
		[]
	);

	const fillForm = async () => {
		const defaultValues = {
			polygonName: null,
			connectionName: null,
			polygon: null,
			country: null,
			city: null
		};

		if (!id) {
			return defaultValues;
		}

		try {
			const { data, status } = await axiosPrivate.post(
				ADMIN.DELIVERY_COMPANY_SETTINGS.GET_POLYGON_COVERAGE_DETAILS,
				{ id }
			);
			if (status === 200) {
				return {
					...defaultValues,
					ID: data.ID ?? null,
					polygonName: data.polygonName ?? null,
					connectionName: data.dcSettingsID ?? null,
					city: data?.city ?? null,
					country: data?.country ?? null,
					polygon: data.polygon ? (viewMode ? coordinatesToStringArray(data.polygon) : data.polygon) : null
				};
			} else {
				throw new Error(data);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
			return defaultValues;
		}
	};

	const formMethods = useForm({
		resolver: yupResolver(tab === 0 ? polygonInfoSchema : polygonSchema),
		defaultValues: async () => await fillForm()
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const isLoading = isFormLoading || isSubmitting;

	const tabs = useMemo(
		() => [
			{
				label: 'polygonInfo',
				component: () => <AreaDetails viewMode={viewMode} isEditMode={mode === 'edit'} />
			},
			{
				label: 'polygon',
				component: () => <AreaLocation viewMode={viewMode} setMap={setMap} />
			}
		],
		[viewMode, setMap]
	);

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const VerticesToStringArray = (vertices) => {
		const verticesArray = [];
		vertices.forEach((vertex, index) => {
			const x = vertex.lat(index);
			const y = vertex.lng(index);
			verticesArray.push(`${x} ${y}`);
		});
		if (verticesArray.length && verticesArray[0] !== verticesArray[verticesArray.length - 1]) {
			verticesArray.push(verticesArray[0]);
		}
		return verticesArray.toString();
	};

	const initPolygon = async (coordinates) => {
		const bounds = new maps.LatLngBounds();
		const coords = [
			{ lat: 25.774, lng: -80.19 },
			{ lat: 18.466, lng: -66.118 },
			{ lat: 32.321, lng: -64.757 }
		];
		const { Polygon } = await maps.importLibrary('maps');

		const area = new Polygon({
			paths: coordinates || coords,
			clickable: !viewMode,
			draggable: !viewMode,
			editable: !viewMode,
			fillColor: '#ee8888',
			fillOpacity: 0.35,
			strokeColor: '#ee8888',
			strokeOpacity: 1.0,
			strokeWeight: 2,
			zIndex: 1
		});
		area.setMap(map);

		coordinates?.forEach((item) => {
			const { lat, lng } = item;
			bounds.extend({ lat: parseFloat(lat), lng: parseFloat(lng) });
		});
		map.fitBounds(bounds);

		const vertices = area.getPath();
		setValue('polygon', VerticesToStringArray(vertices), validateOptions);

		maps.event.addListener(vertices, 'set_at', () => {
			setValue('polygon', VerticesToStringArray(vertices), validateOptions);
		});
		maps.event.addListener(vertices, 'insert_at', () => {
			setValue('polygon', VerticesToStringArray(vertices), validateOptions);
		});
	};

	const initMap = async () => {
		const { DrawingManager } = await maps.importLibrary('drawing');

		const drawingManager = new DrawingManager({
			drawingMode: maps.drawing.OverlayType.POLYGON,
			// drawingControl: true,
			drawingControl: !viewMode,
			drawingControlOptions: {
				position: maps.ControlPosition.TOP_CENTER,
				drawingModes: [maps.drawing.OverlayType.POLYGON]
			},
			polygonOptions: {
				draggable: true,
				editable: true,
				fillColor: '#ee8888',
				fillOpacity: 0.35,
				strokeColor: '#ee8888',
				strokeOpacity: 1.0,
				strokeWeight: 2,
				zIndex: 1
			}
		});
		drawingManager.setMap(map);
		maps.event.addListener(drawingManager, 'overlaycomplete', (polygon) => {
			drawingManager.setDrawingMode(null);
			drawingManager.drawingControl = false;
			drawingManager.setMap(null);

			const path = polygon.overlay.getPath();
			const polygonArray = path.getArray();
			const coordinates = polygonArray.map((coordinate) => ({
				lat: coordinate.lat(),
				lng: coordinate.lng()
			}));
			path.clear();
			initPolygon(coordinates);
		});
	};

	const setPolygonFromInput = () => {
		const polygon = watch('polygon');
		if (!polygon) {
			return;
		}
		if (Array.isArray(polygon)) {
			initPolygon(polygon);
		} else {
			const coordArr = polygon.split(',');
			const latLngArr = coordArr.map((coord) => {
				const splitted = coord.split(' ');
				return { lat: parseFloat(splitted[0]), lng: parseFloat(splitted[1]) };
			});
			initPolygon(latLngArr);
		}
	};

	const polygonArrayToString = (polygon) => {
		if (Array.isArray(polygon)) {
			const polygonArray = [];
			polygon.forEach((item) => {
				polygonArray.push(`${item.lat} ${item.lng}`);
			});
			return polygonArray.toString();
		}
		return polygon;
	};

	useEffect(() => {
		if (tabs?.[tab]?.label === 'polygon') {
			if (map) {
				setPolygonFromInput();
			}
		}
	}, [map, tabs, tab]);

	useEffect(() => {
		if (map) {
			maps.event.addListenerOnce(map, 'idle', id ? setPolygonFromInput : initMap);
		}
	}, [map, id]);

	const onSubmit = async (data) => {
		const URL = id
			? ADMIN.DELIVERY_COMPANY_SETTINGS.UPDATE_POLYGON_COVERAGE
			: ADMIN.DELIVERY_COMPANY_SETTINGS.ADD_POLYGON_COVERAGE_SETTINGS;
		try {
			await axiosPrivate.post(URL, {
				id,
				polygonName: data.polygonName,
				dcSettingsID: data.connectionName,
				city: data.city,
				country: data.country,
				polygon: polygonArrayToString(data.polygon)
			});
			showSuccess();
			dispatch(updateDynamicListData(DYNAMICLISTS.POLYGON_COVERAGE));
			handleCloseModal();
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	return (
		<Modal fullwidth maxWidth="large" name={modalName}>
			<ModalTitle>
				<Typography variant="p1">polygonCoverage</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					{!addMode ? (
						<Tabs data={tabs} />
					) : (
						<>
							<ProgressWizard
								clickable={false}
								stages={[
									{ name: tabs[0].label, icon: faUserTag },
									{ name: tabs[1].label, icon: faBoxCheck }
								]}
								setIndex={setTab}
								index={tab}
								gutterBottom
							/>
							{tabs?.[tab]?.component?.()}
						</>
					)}
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label={'cancel'} variant="outline" onClick={handleCloseModal} />
				{tab !== 0 && addMode && (
					<Button label="previous" variant="outline" icon={faAngleLeft} onClick={() => prevStep()} />
				)}
				{tab < 1 && addMode ? (
					<Button
						icon={faAngleRight}
						label="continue"
						iconPos="right"
						onClick={handleSubmit(nextStep)}
						variant="blue"
						loading={isLoading}
					/>
				) : null}
				{!viewMode && (tab === 1 || !addMode) && (
					<Button
						label={id ? 'save' : 'add'}
						disabled={!isDirty || viewMode}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						iconPos="right"
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

const AreaDetails = ({ viewMode }) => {
	const { countries, cities, fetchCitiesByCountry } = useAddressData();
	const formMethods = useFormContext();
	const {
		setValue,
		watch,
		formState: { isSubmitting, isLoading: isFormLoading }
	} = formMethods;
	const isLoading = isFormLoading || isSubmitting;

	return (
		<>
			<FormItem colSpan={defaultColSpan} label="polygonName" isLoading={isLoading} required>
				<StringInput name="polygonName" viewMode={viewMode} />
			</FormItem>
			<FormItem colSpan={defaultColSpan} label="connectionName" isLoading={isLoading} required>
				<AutoComplete
					autoCompleteName={AUTOCOMPLETE.DELIVERY_COMPANY_SETTING_ALL_CLIENTS}
					value={watch('connectionName')}
					setValue={(value) => {
						setValue('connectionName', value?.ID ?? null, validateOptions);
					}}
					name="connectionName"
					columns={[
						{ accessor: 'ID', width: 90 },
						{ accessor: 'Name', width: 350 },
						{ accessor: 'ClientName', width: 350 },
						{ accessor: 'ClientID', width: 90 }
					]}
					selectionLabelAccessor={['ID', 'Name']}
					viewMode={viewMode}
				/>
			</FormItem>
			<FormItem colSpan={defaultColSpan} label="country" isLoading={isLoading} required>
				<EnumSelect
					labelPrefix="country."
					name="countryName"
					value={watch('country') ?? null}
					disabled={viewMode}
					data={countries}
					renderSelectionValue={(value) => (
						<S.FlagOptionContainer className="selected">
							<span className={`oto-country-flag flag-${value.split('.')[1]?.toLowerCase()}`} />
							<Typography>{value ?? null}</Typography>
						</S.FlagOptionContainer>
					)}
					renderOption={(value) => (
						<S.FlagOptionContainer>
							<span className={`oto-country-flag flag-${value?.toLowerCase()}`} />
							<Typography>{`country.${value?.toUpperCase()}`}</Typography>
						</S.FlagOptionContainer>
					)}
					setValue={(value) => {
						fetchCitiesByCountry(value);

						if (value !== watch('country')) {
							setValue('city', null);
						}

						setValue('country', value ?? null, validateOptions);
					}}
				/>
			</FormItem>
			<FormItem colSpan={defaultColSpan} label="city" isLoading={isLoading} required>
				<EnumSelect
					name="city"
					value={watch('city') ?? null}
					setValue={(value) => setValue('city', value ?? null, validateOptions)}
					disabled={!watch('country') || viewMode}
					data={cities}
				/>
			</FormItem>
		</>
	);
};

const AreaLocation = ({ viewMode, setMap }) => {
	const formMethods = useFormContext();
	const {
		watch,
		formState: { isSubmitting, isLoading: isFormLoading }
	} = formMethods;
	const isLoading = isFormLoading || isSubmitting;

	return (
		<S.AreaLocationContainer>
			{watch('city')?.name && (
				<S.FormArea>
					<Typography autoTranslation={false}>{watch('city').name}</Typography>
				</S.FormArea>
			)}
			<FormItem label="polygon" isLoading={isLoading} toolTip="areaform.polygon.tooltip">
				<TextArea name="polygon" disabled={viewMode} maxLength="65535" className="text-area" />
			</FormItem>
			<div style={{ width: '100%', height: 'calc(var(--vh, 1vh) * 60)' }}>
				<Maps setMap={setMap} />
			</div>
		</S.AreaLocationContainer>
	);
};

export default PolygonCoverageForm;
